import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Download, Loader2, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABEL = {
  name: "Name", phone: "Phone", alt_phone: "Alt phone", email: "Email", locality: "Locality",
  occupation: "Occupation", course_interest: "Course interest", preferred_timings: "Preferred timings",
  source: "Source", source_details: "Source details", source_lead_id: "External lead ID", notes: "Notes",
};

async function downloadCsv(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

export default function ImportExport() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [onDup, setOnDup] = useState("add_enquiry_event");
  const [result, setResult] = useState(null);

  const reset = () => { setStep(1); setPreview(null); setRows([]); setMapping({}); setResult(null); };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/csv/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      // Read full text for commit
      const text = await file.text();
      const parsed = parseCsv(text);
      setRows(parsed);
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const commit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/csv/import/commit", { rows, mapping, on_duplicate: onDup });
      setResult(data); setStep(3);
      qc.invalidateQueries();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => { reset(); setOpen(true); }} data-testid="import-csv-btn">
        <Upload className="w-4 h-4" /> Import CSV
      </Button>
      {user?.role === "admin" && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1" onClick={() => downloadCsv("/csv/export/leads", "launchx_leads.csv")} data-testid="export-leads-btn">
            <Download className="w-4 h-4" /> Export
          </Button>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-brand-blue" /> Import Leads from CSV</DialogTitle>
          <DialogDescription className="sr-only">Upload a CSV, map columns to lead fields, review duplicates and import</DialogDescription></DialogHeader>

          {step === 1 && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-8 text-center">
                <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-500 mb-3">Upload a .csv file (UTF-8, max 2MB / 2000 rows). First row must be headers.</p>
                <Button className="bg-brand-blue hover:bg-blue-700" disabled={loading} onClick={() => fileRef.current?.click()} data-testid="csv-choose-file">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Choose file"}
                </Button>
                <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onFile} />
              </div>
              <p className="text-xs text-slate-400">Tip: include a stable External lead ID column (e.g. a lead form ID) so re-imports don't create duplicates.</p>
            </div>
          )}

          {step === 2 && preview && (
            <div className="space-y-4">
              <div className="text-sm text-slate-500">{preview.total_rows} rows detected. Map columns to lead fields:</div>
              <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto pr-1">
                {preview.fields.map((f) => (
                  <div key={f}>
                    <Label className="text-xs">{FIELD_LABEL[f]}</Label>
                    <Select value={mapping[f] || "none"} onValueChange={(v) => setMapping((m) => { const c = { ...m }; if (v === "none") delete c[f]; else c[f] = v; return c; })}>
                      <SelectTrigger data-testid={`map-${f}`}><SelectValue placeholder="— skip —" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">— skip —</SelectItem>{preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <div>
                <Label className="text-xs">If a row matches an existing lead (by External ID or phone)</Label>
                <Select value={onDup} onValueChange={setOnDup}>
                  <SelectTrigger data-testid="dup-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add_enquiry_event">Add enquiry event (recommended)</SelectItem>
                    <SelectItem value="skip">Skip row</SelectItem>
                    <SelectItem value="create_anyway">Create anyway (separate person)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-lg border overflow-x-auto">
                <div className="text-xs font-semibold px-3 py-2 bg-slate-50 dark:bg-slate-900">Preview (first {preview.preview_rows.length} rows)</div>
                <table className="w-full text-xs">
                  <thead><tr className="border-b">{preview.headers.map((h) => <th key={h} className="text-left px-2 py-1.5 text-slate-500">{h}</th>)}</tr></thead>
                  <tbody>
                    {preview.preview_rows.map((r, i) => (
                      <tr key={i} className="border-b">{preview.headers.map((h) => <td key={h} className="px-2 py-1.5 text-slate-600">{String(r[h] ?? "")}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button className="bg-brand-blue hover:bg-blue-700" disabled={loading || !mapping.name} onClick={commit} data-testid="csv-import-commit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Import ${preview.total_rows} rows`}
                </Button>
              </DialogFooter>
            </div>
          )}

          {step === 3 && result && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 p-4"><CheckCircle2 className="w-5 h-5 mx-auto text-emerald-600 mb-1" /><div className="text-2xl font-extrabold">{result.created}</div><div className="text-xs text-slate-500">Created</div></div>
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950/40 p-4"><CheckCircle2 className="w-5 h-5 mx-auto text-brand-blue mb-1" /><div className="text-2xl font-extrabold">{result.merged}</div><div className="text-xs text-slate-500">Merged as enquiry</div></div>
                <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-4"><AlertTriangle className="w-5 h-5 mx-auto text-slate-500 mb-1" /><div className="text-2xl font-extrabold">{result.skipped + result.errors.length}</div><div className="text-xs text-slate-500">Skipped / errors</div></div>
              </div>
              {result.errors.length > 0 && (
                <div className="max-h-32 overflow-y-auto rounded-lg border p-3 text-xs space-y-1">
                  {result.errors.slice(0, 50).map((e, i) => <div key={i} className="text-red-600">Row {e.row}: {e.reason}</div>)}
                </div>
              )}
              <DialogFooter><Button onClick={() => { setOpen(false); reset(); }} data-testid="csv-done">Done</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const headers = splitLine(lines[0]);
  return lines.slice(1).map((l) => {
    const cells = splitLine(l);
    const obj = {};
    headers.forEach((h, i) => (obj[h] = cells[i] ?? ""));
    return obj;
  });
}
function splitLine(line) {
  const out = []; let cur = ""; let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
    else if (c === "," && !inQ) { out.push(cur); cur = ""; }
    else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}
